import React, { useState, useEffect } from 'react';
import { useEditor } from '../hooks/useEditor';
import { useToast } from '../contexts/ToastContext';
import { useFontManager } from '../hooks/useFontManager';
import { Toolbar } from './Toolbar';
import { ElementLibrary } from './ElementLibrary';
import { Canvas } from './Canvas';
import { PropertiesPanel } from './PropertiesPanel';
import { LayerPanel } from './LayerPanel';
import { ExportModal } from './ExportModal';
import { PreviewModal } from './PreviewModal';
import { FontManager } from './FontManager';
import { Client, BannerSet, BannerSize, ExportOptions } from '../types';
import { exportToJSON, downloadFile, exportBanner } from '../utils/exportUtils';
import { generateThumbnail } from '../utils/thumbnailUtils';
import { ArrowLeft } from 'lucide-react';
import { dataService } from '../services/dataService';
import { ErrorBoundary } from './ErrorBoundary';
import { validateBannerSetForSave } from '../utils/validation';

interface CustomFont {
  id: string;
  name: string;
  family: string;
  url: string;
  format: 'woff' | 'woff2' | 'ttf' | 'otf';
  size: number;
  uploadedAt: Date;
}

interface BannerEditorProps {
  client: Client;
  bannerSet: BannerSet;
  size: BannerSize;
  onUpdateBannerSet: (updates: Partial<BannerSet>) => void;
  onBack: () => void;
}

export const BannerEditor: React.FC<BannerEditorProps> = ({
  client,
  bannerSet,
  size,
  onUpdateBannerSet,
  onBack
}) => {
  const {
    state,
    selectedElement,
    addElement,
    selectElement,
    updateElement,
    deleteElement,
    moveElementLayer,
    setZoom,
    toggleGrid,
    setDataFeed
  } = useEditor(size, bannerSet.dataFeed, bannerSet.dataFeedArray);

  const { customFonts, addCustomFont, removeCustomFont } = useFontManager();
  const [showExportModal, setShowExportModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showFontManager, setShowFontManager] = useState(false);
  const { showSuccess, showError, showWarning, showInfo } = useToast();

  // Add state for error display
  const [saveError, setSaveError] = useState<{
    message: string;
    details: any;
  } | null>(null);

  // Block navigation
  useEffect(() => {
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;

    window.history.pushState = function(data: any, unused: string, url?: string | URL | null) {
      if (saveError) {
        console.log('Navigation blocked due to error:', saveError);
        return;
      }
      return originalPushState.call(this, data, unused, url);
    };

    window.history.replaceState = function(data: any, unused: string, url?: string | URL | null) {
      if (saveError) {
        console.log('Navigation blocked due to error:', saveError);
        return;
      }
      return originalReplaceState.call(this, data, unused, url);
    };

    // Prevent page unload during error
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (saveError) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [saveError]);

  const handleExport = async (options: ExportOptions) => {
    if (state.elements.length === 0) {
      showError('Export Failed', 'Please add some elements to export');
      return;
    }

    try {
      await exportBanner(
        state.elements,
        state.currentSize,
        state.dataFeed,
        state.dataFeedArray,
        options
      );
      showSuccess('Export Complete', 'Your banner has been exported successfully');
    } catch (error) {
      showError('Export Failed', 'An error occurred while exporting your banner');
    }
  };

  const handleSave = async () => {
    if (state.elements.length === 0) {
      showWarning('Nothing to Save', 'Please add some elements before saving');
      return;
    }

    // Reset any previous errors
    setSaveError(null);

    try {
      console.log('Starting save process for banner set:', bannerSet.id);
      
      // Generate thumbnail for the banner
      console.log('Generating thumbnail...');
      const thumbnail = await generateThumbnail(
        state.elements,
        state.currentSize,
        state.dataFeed
      );
      console.log('Thumbnail generated successfully');

      // Only update the specific size that changed
      console.log('Updating sizes array...');
      const updatedSizes = bannerSet.sizes.map(s => 
        s.id === size.id 
          ? { 
              ...s, 
              elements: state.elements, 
              lastModified: new Date(),
              thumbnail,
              version: (s.version || 0) + 1
            }
          : s
      );
      console.log('Sizes array updated');

      // Only send the sizes array if it's different from the current one
      const currentSize = bannerSet.sizes.find(s => s.id === size.id);
      const currentElements = currentSize?.elements || [];
      const currentElementsString = JSON.stringify(currentElements);
      const newElementsString = JSON.stringify(state.elements);
      
      if (currentElementsString !== newElementsString) {
        console.log('Changes detected, preparing update...');
        const updates = { 
          sizes: updatedSizes,
          lastModified: new Date(),
          version: (bannerSet.version || 0) + 1
        };

        // Validate updates before saving
        const validation = validateBannerSetForSave({
          ...bannerSet,
          ...updates
        });

        if (!validation.isValid) {
          console.error('Validation failed:', validation.errors);
          showError('Validation Error', 'The banner set data is invalid. Please check the console for details.');
          return;
        }

        // Update backend first
        console.log('Sending update to backend...');
        try {
          const updatedBannerSet = await dataService.updateBannerSet(bannerSet.id, updates);
          console.log('Backend update successful:', updatedBannerSet);
          
          // Then update local state
          console.log('Updating local state...');
          onUpdateBannerSet(updates);
          console.log('Local state updated');

          showSuccess('Banner Saved', 'Your banner has been saved successfully');
        } catch (error: any) {
          console.error('Backend update failed:', error);
          
          // Store error details
          const errorDetails = {
            timestamp: new Date().toISOString(),
            error: {
              message: error.message,
              stack: error.stack,
              response: error.response ? {
                status: error.response.status,
                data: error.response.data,
                headers: error.response.headers
              } : null
            },
            request: {
              url: error.config?.url,
              method: error.config?.method,
              headers: error.config?.headers?.toJSON(),
              data: error.config?.data
            },
            bannerSetId: bannerSet.id,
            sizeId: size.id,
            url: window.location.href
          };
          
          // Store in sessionStorage
          sessionStorage.setItem('banner_save_error', JSON.stringify(errorDetails));
          
          // Set error state to force display
          setSaveError({
            message: error.response?.data?.message || error.message || 'Failed to save banner',
            details: errorDetails
          });

          // Show error toast
          showError('Save Failed', error.response?.data?.message || error.message || 'Failed to save banner. Please try again.');
          
          // Don't throw the error to prevent navigation
          return;
        }
      } else {
        console.log('No changes detected, skipping save');
        showInfo('No Changes', 'No changes were made to save');
      }
    } catch (error: any) {
      console.error('Failed to save banner:', error);
      
      // Store error details
      const errorDetails = {
        timestamp: new Date().toISOString(),
        error: {
          message: error.message,
          stack: error.stack,
          response: error.response ? {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          } : null
        },
        request: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers?.toJSON(),
          data: error.config?.data
        },
        bannerSetId: bannerSet.id,
        sizeId: size.id,
        url: window.location.href
      };
      
      // Store in sessionStorage
      sessionStorage.setItem('banner_save_error', JSON.stringify(errorDetails));
      
      // Set error state to force display
      setSaveError({
        message: error.response?.data?.message || error.message || 'Failed to save banner',
        details: errorDetails
      });

      // Show error toast
      showError('Save Failed', error.response?.data?.message || error.message || 'Failed to save banner. Please try again.');
    }
  };

  const handleLoad = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          try {
            const template = JSON.parse(event.target?.result as string);
            // This would load the template elements
            showSuccess('Template Loaded', 'Template has been loaded successfully');
          } catch (error) {
            showError('Load Failed', 'Invalid template file format');
          }
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };

  const handleToggleVisibility = (id: string) => {
    const element = state.elements.find(el => el.id === id);
    if (element) {
      const newOpacity = element.styles.opacity === 0 ? 1 : 0;
      updateElement(id, {
        styles: {
          ...element.styles,
          opacity: newOpacity
        }
      });
    }
  };

  const handleFontAdded = (font: CustomFont) => {
    addCustomFont(font);
    showSuccess('Font Added', `${font.name} has been added to your custom fonts`);
  };

  // If there's an error, show it in a modal
  if (saveError) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
          <h2 className="text-xl font-bold text-red-600 mb-4">Save Failed</h2>
          <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
            <p className="text-red-800">{saveError.message}</p>
          </div>
          <pre className="bg-gray-50 p-4 rounded border text-sm overflow-auto mb-4">
            {JSON.stringify(saveError.details, null, 2)}
          </pre>
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => setSaveError(null)}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Close
            </button>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center space-x-4">
            <button
              onClick={onBack}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-xl font-semibold text-gray-900">{bannerSet.name}</h1>
            <span className="text-sm text-gray-500">{size.name}</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowPreviewModal(true)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Preview
            </button>
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Export
            </button>
          </div>
        </div>

        <div className="flex-1 flex overflow-hidden">
          <div className="flex-1 flex flex-col">
            <Toolbar
              currentSize={state.currentSize}
              sizes={[size]}
              zoom={state.zoom}
              showGrid={state.showGrid}
              onSizeChange={() => {}}
              onZoomChange={setZoom}
              onToggleGrid={toggleGrid}
              onExport={() => setShowExportModal(true)}
              onSave={handleSave}
              onLoad={() => {}}
              onPreview={() => setShowPreviewModal(true)}
              hasDataFeed={state.dataFeedArray.length > 1}
            />

            <div className="flex-1 flex">
              <div className="w-64 border-r border-gray-200 bg-white">
                <ElementLibrary
                  onAddElement={addElement}
                  dataFeed={state.dataFeed}
                  onSetDataFeed={setDataFeed}
                />
              </div>

              <div className="flex-1 flex flex-col">
                <div className="flex-1 relative">
                  <Canvas
                    elements={state.elements}
                    size={state.currentSize}
                    zoom={state.zoom}
                    showGrid={state.showGrid}
                    selectedElement={state.selectedElement}
                    dataFeed={state.dataFeed}
                    onSelectElement={selectElement}
                    onUpdateElement={updateElement}
                  />
                </div>

                <div className="h-48 border-t border-gray-200 bg-white">
                  <LayerPanel
                    elements={state.elements}
                    selectedElement={state.selectedElement}
                    onSelectElement={selectElement}
                    onMoveLayer={moveElementLayer}
                    onToggleVisibility={handleToggleVisibility}
                    onUpdateElement={updateElement}
                  />
                </div>
              </div>
            </div>
          </div>

          <PropertiesPanel
            element={selectedElement}
            dataFeed={state.dataFeed}
            onUpdateElement={updateElement}
            onDeleteElement={deleteElement}
            onOpenFontManager={() => setShowFontManager(true)}
          />
        </div>

        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          elements={state.elements}
          size={state.currentSize}
          dataFeed={state.dataFeed}
          dataFeedArray={state.dataFeedArray}
          onExport={handleExport}
        />

        <PreviewModal
          isOpen={showPreviewModal}
          onClose={() => setShowPreviewModal(false)}
          elements={state.elements}
          size={state.currentSize}
          dataFeedArray={state.dataFeedArray}
        />

        <FontManager
          isOpen={showFontManager}
          onClose={() => setShowFontManager(false)}
          onFontAdded={handleFontAdded}
          customFonts={customFonts}
          onDeleteFont={removeCustomFont}
        />
      </div>
    </ErrorBoundary>
  );
};